import axios, { AxiosError } from 'axios';

import { Option } from '../types/option';

export type ProblemJsonViolation = Readonly<{
    propertyPath: string;
    message: string;
}>;

export type ProblemJson = Readonly<{
    title: string;
    status: number;
    violations: ReadonlyArray<ProblemJsonViolation>;
}>;

export const PROBLEM_JSON_SCOPES = {
    category: '/api/categories',
    media: '/api/medias',
} as const;

export type ProblemJsonScope = keyof typeof PROBLEM_JSON_SCOPES;

function isProblemJsonViolation(data: unknown): data is ProblemJsonViolation {
    if (typeof data !== 'object' || data === null) {
        return false;
    }

    const candidate = data as Record<string, unknown>;

    return typeof candidate.propertyPath === 'string' && typeof candidate.message === 'string';
}

export function isProblemJson(data: unknown): data is ProblemJson {
    if (typeof data !== 'object' || data === null) {
        return false;
    }

    const candidate = data as Record<string, unknown>;

    if (typeof candidate.title !== 'string' || typeof candidate.status !== 'number') {
        return false;
    }

    if (candidate.violations === undefined) {
        return true;
    }

    return (
        Array.isArray(candidate.violations) && candidate.violations.every(isProblemJsonViolation)
    );
}

export function parseProblemJson(data: unknown): Option<ProblemJson> {
    if (!isProblemJson(data)) {
        return Option.none();
    }

    return Option.some({
        title: data.title,
        status: data.status,
        violations: data.violations ?? [],
    });
}

export function isScopedApiUrl(url: string | undefined, scope: ProblemJsonScope): boolean {
    if (!url) {
        return false;
    }

    const path = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];

    return path.startsWith(PROBLEM_JSON_SCOPES[scope]);
}

export function getProblemUserMessage(problem: ProblemJson, fallback: string): string {
    const firstViolation = problem.violations[0];

    if (firstViolation?.message) {
        return firstViolation.message;
    }

    return problem.title || fallback;
}

function responseContentType(error: AxiosError): string | undefined {
    const value = error.response?.headers['content-type'];

    if (typeof value === 'string') {
        return value;
    }

    if (Array.isArray(value)) {
        return value[0];
    }

    return undefined;
}

export function isProblemJsonResponse(error: AxiosError): boolean {
    const contentType = responseContentType(error);

    if (contentType?.includes('application/problem+json')) {
        return true;
    }

    return parseProblemJson(error.response?.data).isDefined();
}

export function getScopedApiErrorMessage(
    error: unknown,
    scope: ProblemJsonScope,
    fallback: string,
): Option<string> {
    if (!axios.isAxiosError(error) || !isScopedApiUrl(error.config?.url, scope)) {
        return Option.none();
    }

    const status = error.response?.status;

    if (!status || status >= 500) {
        return Option.some(fallback);
    }

    if (!isProblemJsonResponse(error)) {
        return Option.some(fallback);
    }

    return parseProblemJson(error.response?.data).match(
        () => Option.some(fallback),
        problem => Option.some(getProblemUserMessage(problem, fallback)),
    );
}

const CATEGORY_FLOW_SCOPES: readonly ProblemJsonScope[] = ['category', 'media'];

export function getCategoryFlowErrorMessage(error: unknown, fallback: string): string {
    for (const scope of CATEGORY_FLOW_SCOPES) {
        const message = getScopedApiErrorMessage(error, scope, fallback);

        if (message.isDefined()) {
            return message.get();
        }
    }

    return fallback;
}
